import React, { useState, useEffect, useRef } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqRef = useRef<HTMLDivElement[]>([]);

  const faqs: FAQItem[] = [
    {
      question: 'How do I join KinkAsylum?',
      answer: 'You can search for our alliance in Last War using the alliance code "knki" or fill out the application form on our Join page. Our leadership team will review your application and get back to you.',
    },
    {
      question: 'What are the requirements to join?',
      answer: 'We look for active players who are committed to alliance growth, respectful and team-oriented, willing to participate in alliance activities, and meet our minimum power level requirement. Contact us for specific power level details.',
    },
    {
      question: 'How often do you participate in wars?',
      answer: 'We actively participate in alliance wars and events. Our members coordinate strategies and work together to achieve victory. Participation in wars is expected from all active members.',
    },
    {
      question: 'What happens if I become inactive?',
      answer: 'We understand that real life comes first. However, prolonged inactivity may result in removal after warnings. We encourage members to communicate with leadership if they need to take a break.',
    },
    {
      question: 'Can I invite friends to join?',
      answer: 'Yes! We welcome referrals from existing members. Make sure your friends meet our requirements and follow the same application process. Active members who bring quality recruits are appreciated.',
    },
    {
      question: 'How is leadership structured?',
      answer: 'Our alliance is led by experienced players who coordinate wars, manage members, and maintain our community standards. Leadership positions are earned through dedication, activity, and positive contributions to the alliance.',
    },
    {
      question: 'Do you have a Discord or other communication channel?',
      answer: 'We use in-game communication and may have external channels for coordination. Details about communication platforms are shared with members after joining.',
    },
    {
      question: 'What makes KinkAsylum different?',
      answer: 'We focus on building a strong, respectful community where strategy and teamwork matter. We balance competitive gameplay with a fun, inclusive environment where every member can grow and contribute.',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    faqRef.current.forEach((item: HTMLDivElement | null) => {
      if (item) {
        item.classList.add('fade-in-ready');
        observer.observe(item);
      }
    });

    return () => {
      faqRef.current.forEach((item: HTMLDivElement | null) => {
        if (item) observer.unobserve(item);
      });
    };
  }, []);

  const toggleFAQ = (index: number): void => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="section section-dark">
      <div className="container">
        <h2 className="section-title">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto mt-8">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="mb-4 bg-bg-card rounded-xl border border-border overflow-hidden transition-all duration-300 hover:border-primary"
              ref={(el) => {
                if (el) faqRef.current[index] = el;
              }}
            >
              <button
                className="w-full text-left p-6 flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset transition-colors"
                onClick={() => toggleFAQ(index)}
                aria-expanded={openIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                <h3 className="font-orbitron text-lg text-primary-light pr-4">
                  {faq.question}
                </h3>
                <span
                  className={`text-2xl text-primary transition-transform duration-300 shrink-0 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                >
                  ▼
                </span>
              </button>
              <div
                id={`faq-answer-${index}`}
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 pb-6 text-text-secondary leading-relaxed">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;









